import bearing from "@turf/bearing";
import distance from "@turf/distance";
import { point } from "@turf/helpers";
import lineSliceAlong from "@turf/line-slice-along";
import nearestPointOnLine from "@turf/nearest-point-on-line";
import type { Feature, LineString, Position } from "geojson";
import type { CleanSegment, CompiledCourse } from "./kmlCourse";

// History-aware GPS→course matching over the encoder's clean segments. Clean
// segments never overlap themselves, so each contributes exactly one unimodal
// snap candidate; the matcher scores that small candidate set with a blend of
// geometric, directional, and kinematic evidence instead of trusting a bare
// global nearest-point snap. This keeps riders from teleporting between
// spatially coincident strands of out-and-back / lollipop courses while still
// recovering from a wrong lock. Courses without clean-segment markers fall
// back to the original stateless global snap, byte-for-byte.
//
// Score (lower wins), in meter-equivalents:
//   snap distance
// + bearing mismatch beyond a tolerance (travel bearing from the device's own
//   fix trajectory, falling back to reported heading)
// + kinematic implausibility: course-km moved outside [backtrack tolerance,
//   speed × Δt × slack]
// + turnaround-reversal evidence: regressing km on a segment whose end is a
//   turnaround, after reaching the turn zone, means the rider rounded it
// − a small incumbent bonus (hysteresis)
//
// A lock whose winning candidate repeatedly contradicts a usable travel
// bearing is declared suspect and the matcher re-acquires from scratch, so a
// bad acquisition cannot persist.

/** Snap farther than this from every candidate segment → re-acquire globally. */
export const OFF_COURSE_THRESHOLD_M = 100;
/** Fix gaps beyond this many seconds invalidate history (device offline, page reload). */
export const STALE_WINDOW_S = 300;
/** Rider must be this close (km) to a turnaround boundary before a reversal is trusted. */
export const TURN_ZONE_KM = 0.2;
/** Course-km regression tolerated as jitter before the kinematic penalty applies. */
export const BACKTRACK_TOLERANCE_KM = 0.05;
/** Bearing mismatch under this many degrees is free. */
export const BEARING_TOLERANCE_DEG = 30;
/** Meter-equivalent penalty per degree of bearing mismatch beyond tolerance. */
export const BEARING_WEIGHT_M_PER_DEG = 1.5;
/** Meter-equivalent penalty per km of kinematically implausible course movement. */
export const IMPLAUSIBLE_KM_WEIGHT_M = 50;
/** Reported speed is multiplied by this slack factor when bounding plausible advance. */
export const SPEED_SLACK = 3;
/** Assumed floor speed (km/h) when the device reports none. */
export const MIN_ASSUMED_SPEED_KMH = 5;
/** Free advance (km) added to the plausible envelope regardless of speed. */
export const ADVANCE_GRACE_KM = 0.1;
/** Hysteresis bonus (meters) for staying on the incumbent segment. */
export const INCUMBENT_BONUS_M = 20;
/** Penalty for ignoring turnaround-reversal evidence on the incumbent segment. */
export const TURNAROUND_REVERSAL_PENALTY_M = 250;
/** Minimum travel (km) between fixes for a trajectory bearing to be trusted. */
export const MIN_TRAVEL_FOR_BEARING_KM = 0.015;
/** Consecutive bearing-contradicted wins before the lock is abandoned. */
export const SUSPECT_LIMIT = 3;

export interface MatchState {
  /** Last accepted traversed km (turf arc length). */
  lastKm: number;
  /** Last accepted cleanSegmentNumber; -1 when the course has no segments or the fix was off-course. */
  lastSegment: number;
  /** Unix seconds of the fix that produced lastKm. */
  lastGpstime: number;
  /** Consecutive wins that contradicted a usable travel bearing. */
  suspectCount: number;
}

export interface MatchInput {
  longitude: number;
  latitude: number;
  /** Unix seconds. */
  gpstime: number;
  /** Compass heading in degrees, when the tracker reports one. */
  heading?: number | null;
  /** Reported ground speed, km/h — bounds plausible course movement. */
  speedKmh?: number;
  /** The device's prior fix — preferred source of the travel bearing. */
  previous?: { longitude: number; latitude: number; gpstime: number } | null;
}

/** Arc-length window (turf km) restricting where matches are preferred. */
export interface FocusWindow {
  startKm: number;
  endKm: number;
}

export type GPSFocusOption = "full" | "q1" | "q2" | "q3" | "q4";

export const GPS_FOCUS_OPTIONS: GPSFocusOption[] = ["full", "q1", "q2", "q3", "q4"];

export const GPS_FOCUS_LABELS: Record<GPSFocusOption, string> = {
  full: "Full course",
  q1: "First quarter",
  q2: "Second quarter",
  q3: "Third quarter",
  q4: "Fourth quarter",
};

export function parseGPSFocusOption(value: unknown): GPSFocusOption {
  return typeof value === "string" && (GPS_FOCUS_OPTIONS as string[]).includes(value)
    ? (value as GPSFocusOption)
    : "full";
}

/** null means no restriction. */
export function focusWindowFor(option: GPSFocusOption, totalKm: number): FocusWindow | null {
  if (option === "full" || !Number.isFinite(totalKm) || totalKm <= 0) return null;
  const quarter = { q1: 0, q2: 1, q3: 2, q4: 3 }[option];
  return { startKm: (quarter * totalKm) / 4, endKm: ((quarter + 1) * totalKm) / 4 };
}

export interface MatchOutput {
  traversedKm: number;
  /** Matched cleanSegmentNumber, or null when the course has no segment table. */
  segmentNumber: number | null;
  offCourseMeters: number;
  /** True when the match came from acquisition or the global fallback rather than a scored lock. */
  usedFallback: boolean;
  /** True when a focus window was active but this match landed outside it. */
  outsideFocus: boolean;
}

interface SegmentCandidate {
  segment: CleanSegment;
  /** Traversed km along the full course implied by this candidate. */
  km: number;
  /** Perpendicular snap distance in meters. */
  distM: number;
  /** Local course bearing at the snapped point, degrees. */
  bearingDeg: number;
}

interface SegmentGeometry {
  line: Feature<LineString>;
  startKm: number;
}

// Sub-line slices are cached per compiled course; a recompiled course (new URL)
// is a new object, so its cache starts fresh.
const segmentLineCache = new WeakMap<CompiledCourse, Map<number, SegmentGeometry>>();

function segmentGeometry(course: CompiledCourse, segment: CleanSegment): SegmentGeometry | null {
  let cache = segmentLineCache.get(course);
  if (!cache) {
    cache = new Map();
    segmentLineCache.set(course, cache);
  }
  const cached = cache.get(segment.segmentNumber);
  if (cached) return cached;

  const startKm = Math.min(Math.max(segment.startKm, 0), course.totalKm);
  const endKm = Math.min(Math.max(segment.endKm, 0), course.totalKm);
  if (endKm <= startKm) return null;
  const sub = lineSliceAlong(course.line, startKm, endKm, { units: "kilometers" });
  if (!Array.isArray(sub.geometry.coordinates) || sub.geometry.coordinates.length < 2) return null;
  const geometry: SegmentGeometry = { line: sub, startKm };
  cache.set(segment.segmentNumber, geometry);
  return geometry;
}

function headingDifference(left: number, right: number): number {
  return Math.abs(((left - right + 540) % 360) - 180);
}

function snapToSegment(
  course: CompiledCourse,
  segment: CleanSegment,
  fix: ReturnType<typeof point>,
): SegmentCandidate | null {
  const geometry = segmentGeometry(course, segment);
  if (!geometry) return null;
  const snapped = nearestPointOnLine(geometry.line, fix, { units: "kilometers" });
  const location = snapped.properties.location ?? 0;
  const distKm = snapped.properties.dist ?? Number.POSITIVE_INFINITY;

  const coordinates = geometry.line.geometry.coordinates as Position[];
  const index = Math.min(Math.max(snapped.properties.index ?? 0, 0), coordinates.length - 2);
  const bearingDeg = bearing(point(coordinates[index] as [number, number]), point(coordinates[index + 1] as [number, number]));

  return {
    segment,
    km: Math.min(Math.max(geometry.startKm + location, 0), course.totalKm),
    distM: distKm * 1000,
    bearingDeg,
  };
}

function usableHeading(heading: number | null | undefined): number | null {
  return typeof heading === "number" && Number.isFinite(heading) && heading >= 0 ? heading % 360 : null;
}

/** Direction of travel: fix trajectory when it spans real movement, else reported heading. */
function travelBearingOf(input: MatchInput): number | null {
  const previous = input.previous;
  if (
    previous &&
    Number.isFinite(previous.longitude) &&
    Number.isFinite(previous.latitude) &&
    input.gpstime > previous.gpstime
  ) {
    const from = point([previous.longitude, previous.latitude]);
    const to = point([input.longitude, input.latitude]);
    if (distance(from, to, { units: "kilometers" }) >= MIN_TRAVEL_FOR_BEARING_KM) {
      return (bearing(from, to) + 360) % 360;
    }
  }
  return usableHeading(input.heading);
}

interface ScoreContext {
  travelBearing: number | null;
  /** Present only when trusted history informs kinematics. */
  prev: MatchState | null;
  speedKmh: number;
  gpstime: number;
}

function scoreCandidate(candidate: SegmentCandidate, context: ScoreContext): number {
  let score = candidate.distM;

  if (context.travelBearing !== null) {
    const mismatch = headingDifference(context.travelBearing, candidate.bearingDeg);
    score += Math.max(0, mismatch - BEARING_TOLERANCE_DEG) * BEARING_WEIGHT_M_PER_DEG;
  }

  const prev = context.prev;
  if (prev) {
    const deltaKm = candidate.km - prev.lastKm;
    const dtHours = Math.max(context.gpstime - prev.lastGpstime, 1) / 3600;
    const maxAdvanceKm =
      Math.max(context.speedKmh, MIN_ASSUMED_SPEED_KMH) * SPEED_SLACK * dtHours + ADVANCE_GRACE_KM;
    if (deltaKm > maxAdvanceKm) {
      score += (deltaKm - maxAdvanceKm) * IMPLAUSIBLE_KM_WEIGHT_M;
    } else if (deltaKm < -BACKTRACK_TOLERANCE_KM) {
      score += (-deltaKm - BACKTRACK_TOLERANCE_KM) * IMPLAUSIBLE_KM_WEIGHT_M;
    }

    if (candidate.segment.segmentNumber === prev.lastSegment) {
      score -= INCUMBENT_BONUS_M;
      // Clean segments never self-overlap, so regressing km on the incumbent
      // after reaching a turnaround's turn zone means the rider rounded it —
      // staying put would ignore that evidence.
      if (
        candidate.segment.endsAtTurnaround &&
        Math.max(prev.lastKm, candidate.km) >= candidate.segment.endKm - TURN_ZONE_KM &&
        candidate.km < prev.lastKm - BACKTRACK_TOLERANCE_KM
      ) {
        score += TURNAROUND_REVERSAL_PENALTY_M;
      }
    }
  }

  return score;
}

function segmentIntersectsWindow(segment: CleanSegment, window: FocusWindow): boolean {
  return segment.endKm > window.startKm && segment.startKm < window.endKm;
}

function bestScored(candidates: SegmentCandidate[], context: ScoreContext): SegmentCandidate | null {
  let best: SegmentCandidate | null = null;
  let bestScore = Number.POSITIVE_INFINITY;
  for (const candidate of candidates) {
    if (candidate.distM > OFF_COURSE_THRESHOLD_M) continue;
    const score = scoreCandidate(candidate, context);
    if (score < bestScore) {
      best = candidate;
      bestScore = score;
    }
  }
  return best;
}

/** The original stateless behavior: global nearest-point snap on the full line. */
function globalSnap(course: CompiledCourse, input: MatchInput): { km: number; distM: number } {
  const snapped = nearestPointOnLine(course.line, point([input.longitude, input.latitude]), { units: "kilometers" });
  return {
    km: Math.min(Math.max(snapped.properties.location ?? 0, 0), course.totalKm),
    distM: (snapped.properties.dist ?? 0) * 1000,
  };
}

function buildResult(
  input: MatchInput,
  traversedKm: number,
  segmentNumber: number | null,
  offCourseMeters: number,
  usedFallback: boolean,
  outsideFocus: boolean,
  suspectCount: number,
): { output: MatchOutput; next: MatchState } {
  return {
    output: { traversedKm, segmentNumber, offCourseMeters, usedFallback, outsideFocus },
    next: {
      lastKm: traversedKm,
      lastSegment: segmentNumber ?? -1,
      lastGpstime: input.gpstime,
      suspectCount,
    },
  };
}

/**
 * Match one fix against the course. Pure: pass the previous MatchState for
 * this device (undefined on first sight) and store the returned `next` state
 * for the following fix. An active focus window softly restricts matching to
 * the covered segments — devices with no plausible in-window candidate fall
 * back to the whole course rather than being pinned to the window's edge.
 */
export function matchDeviceToCourse(
  course: CompiledCourse,
  input: MatchInput,
  prev: MatchState | undefined,
  focus?: FocusWindow | null,
): { output: MatchOutput; next: MatchState } {
  if (course.segments.length === 0) {
    const globalResult = globalSnap(course, input);
    return buildResult(input, globalResult.km, null, globalResult.distM, true, false, 0);
  }

  const trusted =
    prev !== undefined &&
    prev.lastSegment >= 0 &&
    prev.suspectCount < SUSPECT_LIMIT &&
    input.gpstime >= prev.lastGpstime &&
    input.gpstime - prev.lastGpstime <= STALE_WINDOW_S;
  const travelBearing = travelBearingOf(input);
  const context: ScoreContext = {
    travelBearing,
    prev: trusted ? prev : null,
    speedKmh: input.speedKmh ?? 0,
    gpstime: input.gpstime,
  };

  const fix = point([input.longitude, input.latitude]);
  const candidates = course.segments
    .map((segment) => snapToSegment(course, segment, fix))
    .filter((candidate): candidate is SegmentCandidate => candidate !== null);

  const focusActive = focus !== undefined && focus !== null;
  const focused = focusActive
    ? candidates.filter((candidate) => segmentIntersectsWindow(candidate.segment, focus))
    : candidates;

  let chosen = bestScored(focused, context);
  let outsideFocus = false;
  if (!chosen && focusActive) {
    // Soft mask: nothing plausible inside the window — match the whole course
    // instead of pinning the rider to the window's nearest edge.
    chosen = bestScored(candidates, context);
    outsideFocus = chosen !== null;
  }

  if (!chosen) {
    const globalResult = globalSnap(course, input);
    const containing = course.segments.find(
      (segment) => globalResult.km >= segment.startKm && globalResult.km <= segment.endKm,
    );
    return buildResult(
      input,
      globalResult.km,
      containing?.segmentNumber ?? null,
      globalResult.distM,
      true,
      focusActive,
      0,
    );
  }

  if (focusActive && !outsideFocus && (chosen.km < focus.startKm || chosen.km > focus.endKm)) {
    // Chosen segment straddles the window boundary and the snap landed outside.
    outsideFocus = true;
  }

  // A win that keeps contradicting a usable travel bearing is a suspect lock;
  // after SUSPECT_LIMIT strikes the history is distrusted and re-acquired.
  // Strikes only accrue on NEW fixes — the periodic recompute of an unchanged
  // sample must not burn through the limit without fresh evidence.
  let suspectCount = 0;
  if (trusted && travelBearing !== null && headingDifference(travelBearing, chosen.bearingDeg) > 90) {
    const previousCount = prev?.suspectCount ?? 0;
    suspectCount = prev !== undefined && input.gpstime > prev.lastGpstime ? previousCount + 1 : Math.max(previousCount, 1);
  }

  return buildResult(
    input,
    chosen.km,
    chosen.segment.segmentNumber,
    chosen.distM,
    !trusted,
    outsideFocus,
    suspectCount,
  );
}
